import { ErrorToastContainer, SuccessToastContainer, WorningToastContainer } from "./CustomFunction";

let whatsappPc = null;
let whatsappStream = null;
let browserStream = null;
let pc = null;
let browserPc = null;
let localMicStream = null;
let activeAudioElements = [];
const ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
  { urls: "stun:stun2.l.google.com:19302" },
  { urls: "stun:stun.relay.metered.ca:80" },
];

let onCallConnectedCallback = null;

export function setOnCallConnected(cb) {
  onCallConnectedCallback = cb;
}

export function isLocalCallActive() {
  return Boolean(browserPc || localMicStream || (activeAudioElements && activeAudioElements.length > 0));
}

export function endCallSession() {
  try {
    // 1. Stop local microphone stream tracks
    if (localMicStream) {
      localMicStream.getTracks().forEach(track => {
        track.stop();
      });
      localMicStream = null;
    }

    // 2. Stop browserStream tracks
    if (browserStream) {
      browserStream.getTracks().forEach(track => {
        track.stop();
      });
      browserStream = null;
    }

    // 3. Stop whatsappStream tracks
    if (whatsappStream) {
      whatsappStream.getTracks().forEach(track => {
        track.stop();
      });
      whatsappStream = null;
    }

    // 4. Close PeerConnections
    if (pc) {
      pc.close();
      pc = null;
    }
    if (browserPc) {
      browserPc.close();
      browserPc = null;
    }
    if (whatsappPc) {
      whatsappPc.close();
      whatsappPc = null;
    }

    // 5. Clean up attached audio elements
    activeAudioElements.forEach(audio => {
      try {
        audio.pause();
        audio.srcObject = null;
        if (audio.parentNode) {
          audio.parentNode.removeChild(audio);
        }
      } catch (e) {
        console.error("Error cleaning audio element:", e);
      }
    });
    activeAudioElements = [];

    console.log("✅ Call session cleaned up successfully.");
  } catch (err) {
    console.error("Error ending call session:", err);
  }
}

export function toggleMicrophoneMute(isMuted) {
  if (localMicStream) {
    localMicStream.getAudioTracks().forEach(track => {
      track.enabled = !isMuted;
    });
  }
  if (browserStream) {
    browserStream.getAudioTracks().forEach(track => {
      track.enabled = !isMuted;
    });
  }
  if (pc) {
    pc.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !isMuted;
      }
    });
  }
}

export function toggleSpeakerMute(isMuted) {
  activeAudioElements.forEach(audio => {
    audio.muted = isMuted;
  });
}

export async function BrowserCandidate(candidate) {
    if (!browserPc) {
        return;
    }

    try {
        await browserPc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
        console.error("Failed to add ICE candidate from browser:", err);
    }
}

export async function createSDPoffer() {
  try {
    // 1️⃣ Create direct browser peer connection with multiple STUN servers
    browserPc = new RTCPeerConnection({ 
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 10
    });

    browserPc.onconnectionstatechange = () => {
      console.log("🔗 WebRTC connectionState:", browserPc.connectionState);
    };
    browserPc.oniceconnectionstatechange = () => {
      console.log("🧊 WebRTC iceConnectionState:", browserPc.iceConnectionState);
    };

    // Handle incoming audio from WhatsApp
    browserPc.ontrack = (event) => {
      console.log("✅ Browser received remote audio track from WhatsApp");
      let audio = activeAudioElements[0];
      if (!audio) {
        audio = new Audio();
        audio.autoplay = true;
        activeAudioElements.push(audio);
        document.body.appendChild(audio);
      }
      audio.srcObject = event.streams[0] || new MediaStream([event.track]);
      audio.play().catch(e => console.warn("Audio autoplay blocked or waiting for user interaction:", e));
    };

    localMicStream = await navigator.mediaDevices.getUserMedia({ 
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
    });
    localMicStream.getTracks().forEach(track => browserPc.addTrack(track, localMicStream));

    const offer = await browserPc.createOffer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await browserPc.setLocalDescription(offer);

    // Wait for local ICE candidate gathering (including public STUN candidates)
    await new Promise((resolve) => {
      if (browserPc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const checkState = () => {
        if (browserPc.iceGatheringState === "complete") {
          browserPc.removeEventListener("icegatheringstatechange", checkState);
          resolve();
        }
      };
      browserPc.addEventListener("icegatheringstatechange", checkState);
      setTimeout(() => {
        browserPc.removeEventListener("icegatheringstatechange", checkState);
        resolve();
      }, 1500);
    });

    const finalOffer = browserPc.localDescription?.sdp || offer.sdp;
    return finalOffer.replace("a=setup:actpass", "a=setup:active");
  } catch (err) {
    ErrorToastContainer("❌ Failed to Establish Connection");
    console.error("❌ Error creating browser SDP:", err);
    return null;
  }
}

export function sanitizeWhatsAppSdp(sdp) {
  if (!sdp) return '';
  // 1. Normalize line endings to \n
  let text = sdp.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let lines = text.split('\n').map(l => l.trim()).filter(l => l.length > 0);

  // 2. Ensure any misplaced session-level ssrc attributes are grouped under m=audio
  let sessionLines = [];
  let mediaLines = [];
  let inMedia = false;

  for (let line of lines) {
    if (line.startsWith('m=')) {
      inMedia = true;
    }
    if (!inMedia && line.startsWith('a=ssrc:')) {
      mediaLines.push(line);
    } else if (inMedia) {
      mediaLines.push(line);
    } else {
      sessionLines.push(line);
    }
  }

  return [...sessionLines, ...mediaLines].join('\r\n') + '\r\n';
}

export async function acceptWhatsAppAnswer(whatsappAnswerSdp) {
  try {
    if (!browserPc) throw new Error("Browser PeerConnection not initialized!");
    console.log("Received WhatsApp answer SDP:", whatsappAnswerSdp);
    const cleanSdp = sanitizeWhatsAppSdp(whatsappAnswerSdp);

    if (browserPc.signalingState === "have-local-offer") {
      await browserPc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: cleanSdp }));
      console.log("✅ WhatsApp answer accepted successfully on browserPc!");
      return true;
    } else if (browserPc.signalingState === "stable") {
      console.log("ℹ️ PeerConnection already in stable state.");
      return true;
    } else {
      await browserPc.setRemoteDescription(new RTCSessionDescription({ type: "answer", sdp: cleanSdp }));
      console.log("✅ WhatsApp answer accepted successfully!");
      return true;
    }
  } catch (err) {
    console.error("❌ Failed to accept WhatsApp answer:", err);
    return false;
  }
}

export async function acceptSDPoffer(whatsappOfferSdp) {
  try {
    if (browserPc) {
      try { browserPc.close(); } catch (_) {}
      browserPc = null;
    }

    browserPc = new RTCPeerConnection({
      iceServers: ICE_SERVERS,
      iceCandidatePoolSize: 10,
    });

    browserPc.onconnectionstatechange = () => {
      console.log("🔗 WebRTC connectionState:", browserPc.connectionState);
      if (browserPc.connectionState === "connected" && typeof onCallConnectedCallback === "function") {
        onCallConnectedCallback();
      }
    };
    browserPc.oniceconnectionstatechange = () => {
      console.log("🧊 WebRTC iceConnectionState:", browserPc.iceConnectionState);
      if (browserPc.iceConnectionState === "connected" && typeof onCallConnectedCallback === "function") {
        onCallConnectedCallback();
      }
    };

    // Handle incoming audio from WhatsApp caller
    browserPc.ontrack = (e) => {
      console.log("✅ Received incoming audio track from WhatsApp caller");
      let audio = activeAudioElements[0];
      if (!audio) {
        audio = new Audio();
        audio.autoplay = true;
        activeAudioElements.push(audio);
        document.body.appendChild(audio);
      }
      audio.srcObject = e.streams[0] || new MediaStream([e.track]);
      audio.play().catch(err => console.warn("Audio play blocked:", err));
    };

    localMicStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      }
    });
    localMicStream.getTracks().forEach(track => browserPc.addTrack(track, localMicStream));

    const cleanSdp = sanitizeWhatsAppSdp(whatsappOfferSdp);
    await browserPc.setRemoteDescription(new RTCSessionDescription({
      type: "offer",
      sdp: cleanSdp,
    }));
    console.log("✅ WhatsApp offer SDP set as remote description.");

    const answer = await browserPc.createAnswer({
      offerToReceiveAudio: true,
      offerToReceiveVideo: false,
    });
    await browserPc.setLocalDescription(answer);

    // Wait for ICE candidate gathering
    await new Promise((resolve) => {
      if (browserPc.iceGatheringState === "complete") {
        resolve();
        return;
      }
      const checkState = () => {
        if (browserPc.iceGatheringState === "complete") {
          browserPc.removeEventListener("icegatheringstatechange", checkState);
          resolve();
        }
      };
      browserPc.addEventListener("icegatheringstatechange", checkState);
      setTimeout(() => {
        browserPc.removeEventListener("icegatheringstatechange", checkState);
        resolve();
      }, 1500);
    });

    const finalAnswer = browserPc.localDescription?.sdp || answer.sdp;
    return finalAnswer.replace("a=setup:actpass", "a=setup:active");
  } catch (err) {
    ErrorToastContainer("❌ Failed to accept call");
    console.error("❌ Error accepting WhatsApp SDP offer:", err);
    return false;
  }
}

export async function checkPermission() {
  try {
    const permission = await navigator.permissions.query({ name: 'microphone' });
    if (permission.state === 'granted') {
        return await checkSpeakerDevices();
    } else if (permission.state === 'denied') {
        ErrorToastContainer("❌ Microphone access denied");
        return false;
    } else {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            SuccessToastContainer('✅ Microphone access granted');
            stream.getTracks().forEach(track => track.stop());
            return await checkSpeakerDevices();
        } catch (error) {
            ErrorToastContainer("❌ Microphone access denied");
            return false;
        }
       
    }
  } catch (error) {
    ErrorToastContainer('Microphone permission check failed:');
    return false;
  }
}


async function checkSpeakerDevices() {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const speakers = devices.filter(d => d.kind === 'audiooutput');

        if (speakers.length > 0) {
            return true;
        } else {
        WorningToastContainer('❌ No speaker devices detected');
        return false;
        }
    } catch (error) {
        ErrorToastContainer('Error checking speakers');
        return false;
    }
}


